import { Alert, Button, Card, Skeleton } from '@ntconsult/aluma-react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useCampanhas, useProgresso, useProgressoCampanhas } from '../hooks';
import type { Campanha } from '../types/Campanha';
import type { Fase, ProgressoDaLista } from '../types/Progresso';
import { CampanhaFormModal } from './CampanhaFormModal';
import { EstadoBadge } from './EstadoBadge';
import { FaseBadge } from './FaseBadge';
import { FunilCompacto } from './FunilCompacto';
import { RodadaConvites } from './RodadaConvites';
import { ACAO_NA_URL, regrasDaFase } from './regrasDaFase';
import { useRotuloDaAcao } from './rotuloDaAcao';

const SEM_ATIVA: readonly Fase[] = ['preparacao', 'pausada', 'fechada'];

/** R20: a ativa; senão, a mais recente entre as de `SEM_ATIVA` com resposta; senão, nenhuma. */
function escolher(campanhas: Campanha[], progresso: ProgressoDaLista[] | undefined) {
  const ativa = campanhas.find((c) => c.ativa);
  if (ativa) return ativa;
  const porId = new Map(progresso?.map((p) => [p.campanhaId, p]));
  return (
    campanhas
      .filter((c) => {
        const p = porId.get(c.id);
        return !!p && SEM_ATIVA.includes(p.fase) && p.responderam >= 1;
      })
      .sort((a, b) => b.criadaEm.localeCompare(a.criadaEm))[0] ?? null
  );
}

function Cartao({ campanha, vinculado }: { campanha: Campanha; vinculado: boolean }) {
  const { t } = useTranslation(['campanhas', 'inicio']);
  const navigate = useNavigate();
  const progresso = useProgresso(campanha.id);
  const p = progresso.data;
  const rotulo = useRotuloDaAcao();
  const abrir = (
    <Button variant="secondary" onClick={() => navigate(`/campanhas/${campanha.id}`)}>
      {t('campanhas:acoes.ver')}
    </Button>
  );
  const titulo = (
    <Link
      to={`/campanhas/${campanha.id}`}
      className="text-lg font-semibold text-foreground underline-offset-2 hover:underline"
    >
      {campanha.nome}
    </Link>
  );

  if (progresso.isPending) return <Skeleton className="h-48 w-full" />;
  // Sem progresso: nome, estado e "Abrir", sem números nem primário (R25).
  if (!p)
    return (
      <Card className="flex flex-col gap-4" data-campanha-do-momento>
        <div className="flex flex-wrap items-center gap-2">
          {titulo}
          <EstadoBadge estado={campanha.estadoEfetivo} />
        </div>
        <div>{abrir}</div>
      </Card>
    );

  // O primário que o detalhe mostra (R21); vai com `?acao=` (R22, spec 36 R13).
  const regras = regrasDaFase(p);
  const primario = regras.primario && ACAO_NA_URL[regras.primario] ? regras.primario : null;
  return (
    <Card className="flex flex-col gap-4" data-campanha-do-momento>
      <div className="flex flex-wrap items-center gap-2">
        {titulo}
        <FaseBadge fase={p.fase} />
      </div>
      <FunilCompacto campanhaId={campanha.id} progresso={p} />
      {p.rodadaEmAndamento && <RodadaConvites campanhaId={campanha.id} />}
      <div className="flex flex-wrap gap-2">
        {primario ? (
          <Button
            data-acao-principal
            onClick={() => navigate(`/campanhas/${campanha.id}?acao=${ACAO_NA_URL[primario]}`)}
          >
            {rotulo(primario, p)}
          </Button>
        ) : (
          abrir
        )}
        {/* Sem `minha-participacao` aqui: ela registra acesso (R23). */}
        {vinculado && p.fase === 'aberta' && (
          <Button variant="secondary" onClick={() => navigate(`/responder/${campanha.id}`)}>
            {t('inicio:responder')}
          </Button>
        )}
      </div>
    </Card>
  );
}

/** Cartão da campanha do momento no Início do administrador (spec 29 R20 a R25). */
export function CampanhaDoMomento({ vinculado }: { vinculado: boolean }) {
  const { t } = useTranslation(['campanhas', 'common']);
  const campanhas = useCampanhas();
  const progresso = useProgressoCampanhas();
  const [criando, setCriando] = useState(false);

  if (campanhas.isPending || progresso.isPending) return <Skeleton className="h-48 w-full" />;
  if (campanhas.isError) return <Alert variant="danger" title={t('common:errors.network')} />;
  const escolhida = escolher(campanhas.data, progresso.data);
  if (escolhida) return <Cartao key={escolhida.id} campanha={escolhida} vinculado={vinculado} />;
  // R24: sem campanha do momento, só "Nova campanha"; salvar vai ao detalhe (spec 28 R11).
  return (
    <div>
      <Button leftIcon={<Plus />} onClick={() => setCriando(true)}>
        {t('campanhas:nova')}
      </Button>
      <CampanhaFormModal open={criando} onClose={() => setCriando(false)} />
    </div>
  );
}
